/**
 * commandCenter 会话管理子模块
 *
 * 负责历史对话的 CRUD、草稿管理、运行态追踪、Agent 标签等。
 */
import { cacheManager } from '@/api'
import { sessionsApi, tracesApi, type SessionResponse, type TraceResponse } from '@/api/sessions'
import { getProjectId, getUserId, isValidProjectId, logger, setProjectId } from '@/utils'
import { ref } from 'vue'
import { useTaskRuntimeStore } from '@/stores/taskRuntime'
import type {
  ConversationDraft,
  ConversationRuntimeStatus,
  HistoryConversation,
} from './types'

// ===== 内部工具函数 =====

/**
 * 从 trace 记录中提取 Agent/Skill 意图标签（去重）
 *
 * 支持两种数据格式：
 * - trace_data.intent 为字符串（旧格式）
 * - trace_data.intent 为对象，包含 operation_type / target_agent 字段（新格式）
 */
function extractAgentTagsFromTraces(traces: TraceResponse[]): string[] {
  const intentSet = new Set<string>()
  for (const trace of traces) {
    const traceData = trace.trace_data as Record<string, unknown> | undefined
    if (!traceData) continue

    // 提取 intent 字段（可能是字符串或对象）
    const rawIntent = traceData.intent
    if (rawIntent) {
      if (typeof rawIntent === 'string') {
        intentSet.add(rawIntent)
      } else if (typeof rawIntent === 'object' && rawIntent !== null) {
        const obj = rawIntent as { operation_type?: string; target_agent?: string }
        if (obj.operation_type) intentSet.add(obj.operation_type)
        if (obj.target_agent) intentSet.add(obj.target_agent)
      }
    }
    // 提取顶层 operation_type（如 announcement）
    const opType = traceData.operation_type as string | undefined
    if (opType && opType !== 'clarification') {
      intentSet.add(opType)
    }
  }
  return Array.from(intentSet)
}

/**
 * 解析会话运行态的最终展示值。
 *
 * 历史详情缺失/加载失败属于前端在回放阶段确认的更强信号，
 * 不能再被列表接口里较粗粒度的 completed 状态覆盖。
 */
function resolveMergedRuntimeState(
  apiStatus?: ConversationRuntimeStatus,
  draftStatus?: ConversationRuntimeStatus,
  existingStatus?: ConversationRuntimeStatus,
): ConversationRuntimeStatus | undefined {
  if (draftStatus === 'failed' || draftStatus === 'cancelled' || draftStatus === 'clarification') {
    return draftStatus
  }

  if (apiStatus) {
    return apiStatus
  }

  return draftStatus ?? existingStatus
}

/**
 * 解析会话运行态错误信息。
 *
 * 当本地草稿已经记录了明确的失败原因时，优先展示该原因，
 * 避免刷新列表后被空的后端错误信息抹掉。
 */
function resolveMergedRuntimeError(
  apiError?: string,
  draftStatus?: ConversationRuntimeStatus,
  draftError?: string,
  existingError?: string,
): string | undefined {
  if ((draftStatus === 'failed' || draftStatus === 'cancelled' || draftStatus === 'clarification') && draftError) {
    return draftError
  }

  return apiError ?? draftError ?? existingError
}

/**
 * 创建会话管理子模块的状态和操作
 *
 * 返回所有会话相关的 state / getters / actions。
 */
export function useConversationSlice() {
  // ===== State =====
  const historyConversations = ref<HistoryConversation[]>([])
  const activeConversationId = ref<string | null>(null)
  const conversationDrafts = ref<Record<string, ConversationDraft>>({})

  // 会话加载状态
  const sessionsLoading = ref(false)
  const sessionsError = ref<string | null>(null)

  // 分页信息
  const sessionsPagination = ref({
    total: 0,
    page: 1,
    page_size: 20,
    hasMore: false
  })

  const currentProjectId = ref(getProjectId())
  const currentUserId = ref(getUserId())

  // ===== 内部方法 =====

  function getConversationDraft(sessionId: string | undefined | null): ConversationDraft | undefined {
    if (!sessionId) return undefined
    return conversationDrafts.value[sessionId]
  }

  function mergeConversationState(
    conversation: HistoryConversation,
    existingConversation?: HistoryConversation
  ): HistoryConversation {
    const draft = getConversationDraft(conversation.session_id)
    return {
      ...conversation,
      query: draft?.query ?? existingConversation?.query ?? conversation.query,
      preview: draft?.preview ?? existingConversation?.preview ?? conversation.preview,
      runtime_status: resolveMergedRuntimeState(
        conversation.runtime_status,
        draft?.runtime_status,
        existingConversation?.runtime_status,
      ),
      runtime_error: resolveMergedRuntimeError(
        conversation.runtime_error,
        draft?.runtime_status,
        draft?.runtime_error,
        existingConversation?.runtime_error,
      ),
    }
  }

  /**
   * 将后端 last_trace_status 映射为前端 ConversationRuntimeStatus
   */
  function mapTraceStatusToRuntime(
    traceStatus: string | undefined
  ): ConversationRuntimeStatus | undefined {
    if (!traceStatus) return undefined
    switch (traceStatus) {
      case 'failed':
        return 'failed'
      case 'cancelled':
        return 'cancelled'
      case 'running':
      case 'pending':
        return 'running'
      case 'completed':
        return 'completed'
      default:
        return undefined
    }
  }

  // 将后端 SessionResponse 转换为 HistoryConversation
  function sessionToConversation(session: SessionResponse): HistoryConversation {
    return mergeConversationState({
      id: session.id,
      session_id: session.session_id,
      title: session.title || '未命名会话',
      query: session.first_query || session.title || '',
      timestamp: session.last_message_at || session.created_at,
      agent: '洞察',
      preview: undefined,
      message_count: session.message_count,
      last_message_at: session.last_message_at,
      agents: session.agent_tags || undefined,
      // 后端一次性返回的 trace 状态，列表加载时即可正确显示
      runtime_status: mapTraceStatusToRuntime(session.last_trace_status),
    })
  }

  // ===== Actions =====

  // 更新项目 ID（切换项目时调用）
  function updateProjectId(projectId: string) {
    if (!isValidProjectId(projectId)) {
      logger.warn('[commandCenter] 拒绝非法 projectId，忽略 updateProjectId 调用', { projectId })
      return
    }
    if (currentProjectId.value !== projectId) {
      setProjectId(projectId)
      currentProjectId.value = projectId
      // 清空历史对话列表，等待重新加载
      historyConversations.value = []
      sessionsPagination.value = { total: 0, page: 1, page_size: 20, hasMore: false }
    }
  }

  // 更新用户 ID
  function updateUserId(userId: string) {
    if (currentUserId.value !== userId) {
      localStorage.setItem('userId', userId)
      currentUserId.value = userId
      // 清空历史对话列表，等待重新加载
      historyConversations.value = []
      sessionsPagination.value = { total: 0, page: 1, page_size: 20, hasMore: false }
    }
  }

  function upsertConversationDraft(sessionId: string, draft: ConversationDraft) {
    if (!sessionId) return

    const nextDraft: ConversationDraft = {
      ...(conversationDrafts.value[sessionId] || {}),
      ...draft,
    }

    if (!nextDraft.runtime_error) {
      delete nextDraft.runtime_error
    }

    conversationDrafts.value = {
      ...conversationDrafts.value,
      [sessionId]: nextDraft,
    }

    const conversation = historyConversations.value.find(c => c.session_id === sessionId)
    if (conversation) {
      Object.assign(conversation, nextDraft)
    }
  }

  function setConversationRuntimeState(
    sessionId: string,
    status: ConversationRuntimeStatus,
    error?: string
  ) {
    upsertConversationDraft(sessionId, {
      runtime_status: status,
      runtime_error: error || undefined,
    })
  }

  function addHistoryConversation(conversation: HistoryConversation) {
    historyConversations.value.unshift(mergeConversationState(conversation))
  }

  function setActiveConversation(id: string | null) {
    if (!id) {
      activeConversationId.value = null
      return
    }

    const conversation = historyConversations.value.find(c => c.session_id === id || c.id === id)
    activeConversationId.value = conversation?.session_id || id
  }

  function loadConversation(id: string) {
    const conversation = historyConversations.value.find(c => c.session_id === id || c.id === id)
    if (conversation) {
      activeConversationId.value = conversation.session_id
    }
    return conversation
  }

  /**
   * 后台预加载 Agent 标签
   *
   * 对没有 agents 数据的会话，批量拉取 traces 提取意图标签。
   * 每批最多并发 5 个请求，避免冲击后端。不阻塞 UI。
   */
  async function _prefetchAgentTags(conversations: HistoryConversation[]) {
    // 筛选需要加载标签的会话（没有 agents 且有 session_id）
    const needTags = conversations.filter(c => !c.agents && c.session_id)
    if (needTags.length === 0) return

    // 分批并发（每批 5 个）
    const BATCH_SIZE = 5
    for (let i = 0; i < needTags.length; i += BATCH_SIZE) {
      const batch = needTags.slice(i, i + BATCH_SIZE)
      await Promise.allSettled(
        batch.map(async (conv) => {
          try {
            const resp = await tracesApi.list({
              session_id: conv.session_id,
              page: 1,
              page_size: 20  // 只取最近的 traces 足够提取标签
            })
            const traces = Array.isArray(resp) ? resp : (resp?.data || [])
            const tags = extractAgentTagsFromTraces(traces as TraceResponse[])
            if (tags.length > 0) {
              // 更新本地 store
              const target = historyConversations.value.find(c => c.session_id === conv.session_id)
              if (target) {
                target.agents = tags
              }
              // 异步持久化到 PG（后端可用时生效）
              sessionsApi.updateAgentTags(conv.session_id, tags, currentProjectId.value).catch((err: unknown) => {
                logger.warn('[commandCenter] 持久化 agent tags 失败:', err)
              })
            }
          } catch (err) {
            // 单个会话失败不影响其他
            logger.warn('[commandCenter] 预加载 agent tags 失败, session:', conv.session_id, err)
          }
        })
      )
    }
  }

  /**
   * 后台预加载会话运行态
   *
   * 当后端 batch_get_last_status 降级（未返回 last_trace_status）时，
   * 逐个会话查询最后一条 trace 的状态并回填。
   * 使用 resp.total 精确计算最后一页页码，避免依赖可能不准确的 message_count。
   */
  async function _prefetchConversationRuntimeStates(conversations: HistoryConversation[]) {
    const needRuntimeState = conversations.filter(conversation =>
      conversation.session_id
      && !conversation.runtime_status
      && (conversation.message_count || 0) > 0
    )
    if (needRuntimeState.length === 0) return

    const BATCH_SIZE = 5
    for (let i = 0; i < needRuntimeState.length; i += BATCH_SIZE) {
      const batch = needRuntimeState.slice(i, i + BATCH_SIZE)
      await Promise.allSettled(
        batch.map(async (conversation) => {
          try {
            const target = historyConversations.value.find(
              item => item.session_id === conversation.session_id
            )
            if (!target || target.runtime_status) {
              return
            }

            // 先请求 page=1, page_size=1 以获取 total（不依赖可能不准确的 message_count）
            const firstResp = await tracesApi.list({
              session_id: conversation.session_id,
              page: 1,
              page_size: 1,
            })
            const total = firstResp.total || 0
            const firstTraces = firstResp.data || []

            if (total === 0 || firstTraces.length === 0) {
              setConversationRuntimeState(conversation.session_id, 'failed', '历史对话详情缺失')
              return
            }

            // 确定最后一条 trace（后端按 created_at ASC 排序，最后一页 = 最新记录）
            let lastTrace: TraceResponse
            if (total <= 1) {
              lastTrace = firstTraces[0]
            } else {
              const lastResp = await tracesApi.list({
                session_id: conversation.session_id,
                page: total,
                page_size: 1,
              })
              const lastTraces = lastResp.data || []
              lastTrace = lastTraces[0] || firstTraces[0]
            }

            // 异步期间可能已被其他流程设置，再次检查
            if (target.runtime_status) return

            switch (lastTrace.status) {
              case 'failed':
                setConversationRuntimeState(conversation.session_id, 'failed')
                break
              case 'cancelled':
                setConversationRuntimeState(conversation.session_id, 'cancelled')
                break
              case 'running':
              case 'pending':
                setConversationRuntimeState(conversation.session_id, 'running')
                break
              case 'completed':
                setConversationRuntimeState(conversation.session_id, 'completed')
                break
              default:
                break
            }
          } catch (error) {
            logger.warn('[CommandCenter] 预加载会话运行态失败:', {
              sessionId: conversation.session_id,
              error,
            })
          }
        })
      )
    }
  }

  // 从后端加载会话列表
  async function fetchSessions(options?: { page?: number; refresh?: boolean }) {
    const page = options?.page || 1
    const refresh = options?.refresh || false

    if (sessionsLoading.value && !refresh) return

    sessionsLoading.value = true
    sessionsError.value = null

    try {
      const response = await sessionsApi.list({
        project_id: currentProjectId.value,
        user_id: currentUserId.value,
        page,
        page_size: sessionsPagination.value.page_size
      })

      const existingConversationMap = new Map(
        historyConversations.value.map(conversation => [conversation.session_id, conversation])
      )
      const conversations = response.data.map((session) =>
        mergeConversationState(
          sessionToConversation(session),
          existingConversationMap.get(session.session_id)
        )
      )

      if (refresh || page === 1) {
        // 刷新或第一页：替换列表
        historyConversations.value = conversations
      } else {
        // 追加加载
        historyConversations.value.push(...conversations)
      }

      sessionsPagination.value = {
        total: response.total,
        page: response.page,
        page_size: response.page_size,
        hasMore: (response.page * response.page_size) < response.total
      }

      // 自动预加载 Agent 标签和运行态（不阻塞列表渲染）
      void _prefetchAgentTags(conversations)
      void _prefetchConversationRuntimeStates(conversations)

      // 从会话列表的 active_task 字段恢复长任务映射（浏览器刷新场景）
      // 只在首页（page=1）或刷新时 hydrate，避免追加加载时重复注册
      if (refresh || page === 1) {
        const taskRuntime = useTaskRuntimeStore()
        taskRuntime.hydrateFromSessions(response.data)
      }
    } catch (error) {
      logger.error('Failed to fetch sessions:', error)
      sessionsError.value = error instanceof Error ? error.message : '加载会话列表失败'
    } finally {
      sessionsLoading.value = false
    }
  }

  // 加载更多会话
  async function loadMoreSessions() {
    if (!sessionsPagination.value.hasMore || sessionsLoading.value) return
    await fetchSessions({ page: sessionsPagination.value.page + 1 })
  }

  // 刷新会话列表
  async function refreshSessions() {
    await fetchSessions({ page: 1, refresh: true })
  }

  /**
   * 创建新会话（同步等待版本，仅供需要等待结果的场景使用）
   */
  async function createSession(title?: string): Promise<string | null> {
    try {
      const response = await sessionsApi.create({
        project_id: currentProjectId.value,
        user_id: currentUserId.value,
        title: title || '新会话',
        source: 'user'
      })

      const conversation = sessionToConversation(response)
      historyConversations.value.unshift(conversation)
      cacheManager.clearSessions()

      return response.session_id
    } catch (error) {
      logger.warn('[CommandCenter] 会话创建失败:', error)
      return null
    }
  }

  // 跟踪后台 session 创建的 Promise，供 agent tags 持久化等待
  const _pendingSessionCreations = new Map<string, Promise<void>>()

  /**
   * 等待指定 session 的后台创建完成
   *
   * 如果该 session 没有挂起的创建任务则立即 resolve。
   * 超时后静默 resolve（不阻塞调用方），避免因创建永久失败导致后续操作卡死。
   */
  function _waitForSessionCreation(sessionId: string, timeoutMs = 15000): Promise<void> {
    const pending = _pendingSessionCreations.get(sessionId)
    if (!pending) return Promise.resolve()
    // 带超时保护，避免无限等待
    return Promise.race([
      pending,
      new Promise<void>(resolve => setTimeout(resolve, timeoutMs)),
    ])
  }

  /**
   * 后台异步创建会话（非阻塞，带指数退避重试）
   *
   * 用于消息发送流程：先用临时 UUID 让用户零等待，后台将会话持久化到 PG。
   * 创建成功后自动替换临时 ID 并更新会话列表。
   * 创建期间的 Promise 会注册到 _pendingSessionCreations，
   * 供 updateAgentTags 等依赖操作等待。
   *
   * @param tempId 前端已分配的临时 UUID
   * @param title 会话标题
   * @param maxRetries 最大重试次数
   */
  function createSessionInBackground(
    tempId: string,
    title: string,
    maxRetries = 2
  ): void {
    const creationPromise = (async () => {
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const response = await sessionsApi.create({
            project_id: currentProjectId.value,
            user_id: currentUserId.value,
            session_id: tempId,
            title: title || '新会话',
            source: 'user'
          })

          replaceConversationSessionId(tempId, response.session_id)
          cacheManager.clearSessions()
          logger.info('[CommandCenter] 后台会话创建成功:', response.session_id)
          return
        } catch (error) {
          if (attempt < maxRetries) {
            const delay = 2000 * Math.pow(2, attempt)
            logger.warn(
              `[CommandCenter] 后台会话创建失败 (${attempt + 1}/${maxRetries + 1})，${delay}ms 后重试`,
              error
            )
            await new Promise(resolve => setTimeout(resolve, delay))
          } else {
            logger.error('[CommandCenter] 后台会话创建最终失败，使用临时 UUID:', tempId, error)
          }
        }
      }
    })()

    _pendingSessionCreations.set(tempId, creationPromise)
    // 无论成功失败，完成后清理引用
    creationPromise.finally(() => {
      _pendingSessionCreations.delete(tempId)
    })
  }

  /**
   * 替换会话的 session_id（后台创建成功后将临时 UUID 映射到真实 ID）
   */
  function replaceConversationSessionId(oldId: string, newId: string): void {
    const conversation = historyConversations.value.find(c => c.session_id === oldId)
    if (conversation) {
      conversation.id = newId
      conversation.session_id = newId
    }
    if (conversationDrafts.value[oldId]) {
      const nextDrafts = {
        ...conversationDrafts.value,
        [newId]: {
          ...(conversationDrafts.value[newId] || {}),
          ...conversationDrafts.value[oldId],
        },
      }
      delete nextDrafts[oldId]
      conversationDrafts.value = nextDrafts
    }
    if (activeConversationId.value === oldId) {
      activeConversationId.value = newId
    }
    if (typeof window !== 'undefined' && oldId !== newId) {
      window.dispatchEvent(new CustomEvent('command-center-session-replaced', {
        detail: { oldId, newId },
      }))
    }
  }

  // 更新会话标题
  async function updateSessionTitle(sessionId: string, title: string) {
    try {
      await sessionsApi.update(sessionId, { title })

      // 更新本地列表
      const conversation = historyConversations.value.find(c => c.session_id === sessionId)
      if (conversation) {
        conversation.title = title
      }

      // 清除缓存
      cacheManager.clearSessions()
    } catch (error) {
      logger.error('Failed to update session title:', error)
    }
  }

  /**
   * 更新会话的 Agent/Skill 标签列表（本地 + PG 持久化）
   * 将新的 intent 追加到会话的 agents 数组中（去重），并异步同步到后端 PG
   * @param sessionId - 会话 session_id
   * @param intent - 新检测到的意图类型（如 smart_query, dashboard_builder 等）
   */
  function updateConversationAgents(sessionId: string, intent: string) {
    const conversation = historyConversations.value.find(c => c.session_id === sessionId)
    if (!conversation) return

    if (!conversation.agents) {
      conversation.agents = []
    }
    // 去重添加
    if (!conversation.agents.includes(intent)) {
      conversation.agents.push(intent)
    }
    // 等待后台 session 创建完成后再持久化（避免竞态 404）
    _waitForSessionCreation(sessionId).then(() => {
      sessionsApi.updateAgentTags(sessionId, [intent], currentProjectId.value).catch(err => {
        logger.warn('[CommandCenter] 持久化 agent_tags 失败:', err)
      })
    })
  }

  /**
   * 批量设置会话的 Agent/Skill 标签列表（本地 + PG 持久化）
   * @param sessionId - 会话 session_id
   * @param agents - 完整的 agents 列表
   */
  function setConversationAgents(sessionId: string, agents: string[]) {
    const conversation = historyConversations.value.find(c => c.session_id === sessionId)
    if (!conversation) return
    conversation.agents = agents
    // 等待后台 session 创建完成后再持久化（避免竞态 404）
    _waitForSessionCreation(sessionId).then(() => {
      sessionsApi.updateAgentTags(sessionId, agents, currentProjectId.value).catch(err => {
        logger.warn('[CommandCenter] 持久化 agent_tags 失败:', err)
      })
    })
  }

  // 删除会话
  async function deleteSession(sessionId: string) {
    try {
      await sessionsApi.delete([sessionId])

      // 从本地列表移除
      const index = historyConversations.value.findIndex(c => c.session_id === sessionId)
      if (index > -1) {
        historyConversations.value.splice(index, 1)
      }

      if (conversationDrafts.value[sessionId]) {
        const nextDrafts = { ...conversationDrafts.value }
        delete nextDrafts[sessionId]
        conversationDrafts.value = nextDrafts
      }

      // 如果删除的是当前激活的会话，清空激活状态
      if (activeConversationId.value === sessionId) {
        activeConversationId.value = null
      }

      // 清除缓存
      cacheManager.clearSessions()
    } catch (error) {
      logger.error('Failed to delete session:', error)
    }
  }

  return {
    // State
    historyConversations,
    activeConversationId,
    sessionsLoading,
    sessionsError,
    sessionsPagination,
    currentProjectId,
    currentUserId,
    // Actions
    updateProjectId,
    updateUserId,
    upsertConversationDraft,
    setConversationRuntimeState,
    addHistoryConversation,
    setActiveConversation,
    loadConversation,
    fetchSessions,
    loadMoreSessions,
    refreshSessions,
    createSession,
    createSessionInBackground,
    replaceConversationSessionId,
    updateSessionTitle,
    updateConversationAgents,
    setConversationAgents,
    deleteSession,
  }
}
